<template>
  <rv-dialog
    ref="dialogRef"
    class="stop-job-import-dialog"
    data-automation="stop-job-import-dialog"
    v-on="$attrs"
    @hide="onDialogHide"
    @ok="onOk"
  >
    <template #title>
      <q-icon
        color="grey-2"
        name="error"
        size="33px"
      />
      <div class="text-subtitle1 q-ml-sm">
        {{ $t('general.actions.stopImport') }}
      </div>
    </template>
    <div>{{ $t('projects.importJobs.cancelImportDescription') }}</div>
    <div
      v-if="job?.status === ImportJobStatus.RUNNING || job?.isDone"
      class="q-mt-sm"
    >
      <rv-radio
        v-model="removeImport"
        class="stop-job-import-dialog__radio q-mb-sm"
        data-automation="stop-job-import-dialog__rollback"
        dense
        :val="Events.Delete"
      >
        <div class="stop-job-import-dialog__radio-content">
          {{ $t('projects.importJobs.delete') }}
        </div>
      </rv-radio>
      <rv-radio
        v-model="removeImport"
        class="stop-job-import-dialog__radio"
        data-automation="stop-job-import-dialog__keep"
        dense
        :val="Events.Keep"
      >
        <div class="stop-job-import-dialog__radio-content">
          {{ $t('projects.importJobs.keep',[job?.amountSuccess, job?.amountTotal]) }}
        </div>
      </rv-radio>
    </div>

    <template #actions>
      <rv-button
        class="rv-confirm-dialog__cancel-btn"
        data-automation="stop-job-import-dialog__cancel"
        :label="$t('general.actions.cancel')"
        secondary
        size="sm"
        @click="onDialogHide"
      />

      <rv-button
        class="rv-confirm-dialog__confirm-btn"
        color="primary"
        data-automation="stop-job-import-dialog__confirm"
        :disable="!removeImport || isWaiting"
        :label="$t('general.actions.confirm')"
        size="sm"
        @click="onOk"
      />
    </template>
  </rv-dialog>
</template>

<script lang="ts" setup>
import { useDialogPluginComponent } from 'quasar'
import {
  computed,
  type PropType,
  ref,
} from 'vue'
import { useImportJobStatus } from '~composables/useImportJobStatus'
import type { ImportJob } from '~domain/io/ImportJob'
import { ImportJobStatus } from '~domain/io/ImportJob'
import { useProjectsStore } from '~stores/projectsStore/useProjectsStore'
import { useDatapoolsStore } from '~stores/useDatapoolsStore'
import { useImportJobsStore } from '~stores/useImportJobsStore'


enum Events {
  Delete = 'delete',
  Keep = 'keep',
}

const props = defineProps({
  job: {
    required: true,
    type: Object as PropType<ImportJob>,
  },
  projectId: {
    required: true,
    type: String as PropType<string>,
  },
})

defineEmits([...useDialogPluginComponent.emits])

const {
  getImportJob,
  getImportJobByJobId,
} = useImportJobsStore()

const job = computed(() => props.job.jobId ? getImportJobByJobId(props.job.jobId) : getImportJob(props.job.importId))
const { isWaiting } = useImportJobStatus(job)

const {
  dialogRef,
  onDialogHide,
} = useDialogPluginComponent()
const { cancelImportJob } = useImportJobsStore()
const {
  deleteProjectSamplesByImportJob,
  loadProjectDatapools,
} = useProjectsStore()
const {
  setDatapoolTags,
  tags,
} = useDatapoolsStore()

const removeImport = ref(Events.Keep)

function onOk (confirm: boolean): void {
  const rollback = removeImport.value === Events.Delete

  if (job.value?.isDone) {
    const shouldBeRemoved = confirm && rollback
    if (shouldBeRemoved) {
      void deleteProjectSamplesByImportJob(props.projectId, job.value)
      void loadProjectDatapools(props.projectId)
      removeNameFromDatapoolTags()
    }
  } else if (confirm) {
    void cancelImportJob(job.value?.jobId ?? props.job.importId, rollback)
    removeNameFromDatapoolTags()
  }

  onDialogHide()
}

function removeNameFromDatapoolTags () {
  setDatapoolTags(props.job.datapoolId, (tags.get(props.job.datapoolId) ?? []).filter(tag => tag !== props.job.name))
}
</script>

<style lang="sass" scoped>
.stop-job-import-dialog
  &__radio
    ::v-deep(.q-radio__inner)
      align-self: flex-start
      margin-top: 3px

  &__radio-content
    line-height: 24px
</style>
